import React, { useState, useEffect } from "react";
import {
  deleteMessageContactForm,
  markMessageContactForm,
  getAllMessagesContactForm,
} from "../../services/contactformServices";
import { useContactForm } from "../../contexts/ContactFormContext";
import { MdMessage } from "react-icons/md";
import ButtonEyeNoRead from "../../components/Button/ButtonEyeNoRead";
import ButtonEyeRead from "../../components/Button/ButtonEyeRead";
import ButtonDelete from "../../components/Button/ButtonDelete";
import "./styles.css";

const ContactFormDisplay = ({ userId }) => {
  const [messages, setMessages] = useState([]);
  const { unreadCountContactForm, setUnreadCountContactForm } =
    useContactForm();

  const loadMessages = async () => {
    if (!userId) {
      console.error("L'ID de l'utilisateur n'est pas trouvé");
      return;
    }

    try {
      const data = await getAllMessagesContactForm(userId);
      const sorted = (data || []).sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setMessages(sorted);
    } catch (error) {
      console.error("Erreur lors du chargement des messages:", error);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [userId]);

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessageContactForm(messageId);
      await loadMessages();
    } catch (error) {
      console.error("Erreur lors de la suppression du message:", error);
    }
  };

  const toggleReadStatus = async (messageId) => {
    try {
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;

      await markMessageContactForm(messageId);

      setMessages((prevMessages) =>
        prevMessages.map((m) =>
          m.id === messageId ? { ...m, read_message: !m.read_message } : m
        )
      );

      setUnreadCountContactForm((prevCount) =>
        message.read_message ? prevCount + 1 : Math.max(0, prevCount - 1)
      );
    } catch (error) {
      console.error(
        "Erreur lors du basculement de l'état de lecture du message:",
        error
      );
    }
  };

  return (
    <div className="w-full font-montserrat">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <MdMessage
            className="text-xl text-primary"
            title="Message du formulaire de contact"
          />
          {unreadCountContactForm > 0 && (
            <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
              {unreadCountContactForm}
            </span>
          )}
        </div>
        <h3 className="text-sm sm:text-base font-semibold">
          Messages du formulaire de contact
        </h3>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-4 text-sm bg-slate-50 dark:bg-slate-900/80 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          Aucun message de formulaire.
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden sm:block">
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-200 dark:bg-slate-800">
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Date
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Détails
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((message) => (
                    <tr
                      key={message.id}
                      className={`border-t border-slate-200 dark:border-slate-700 ${
                        message.read_message
                          ? "bg-white dark:bg-slate-900"
                          : "bg-primary/5 dark:bg-primary/15"
                      }`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700 dark:text-slate-200">
                        {new Date(message.created_at).toLocaleDateString(
                          "fr-FR",
                          {
                            year: "2-digit",
                            month: "2-digit",
                            day: "2-digit",
                          }
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-200">
                        <div className="font-semibold">{message.name}</div>
                        <div className="text-xs text-primary font-bold italic">
                          {message.email}
                        </div>
                        <div className="text-xs mt-1">{message.message}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex justify-end gap-1.5">
                          {message.read_message ? (
                            <ButtonEyeRead
                              onClick={() => toggleReadStatus(message.id)}
                              hoverLabel="Marquer comme non lu"
                            />
                          ) : (
                            <ButtonEyeNoRead
                              onClick={() => toggleReadStatus(message.id)}
                              hoverLabel="Marquer comme lu"
                            />
                          )}
                          <ButtonDelete
                            onClick={() => handleDeleteMessage(message.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile */}
          <div className="block sm:hidden space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`rounded-xl border border-slate-200 dark:border-slate-700 p-3 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                  message.read_message
                    ? "bg-white dark:bg-slate-900"
                    : "bg-primary/5 dark:bg-primary/15"
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {new Date(message.created_at).toLocaleDateString("fr-FR", {
                      year: "2-digit",
                      month: "2-digit",
                      day: "2-digit",
                    })}
                  </span>
                  <div className="flex gap-1.5">
                    {message.read_message ? (
                      <ButtonEyeRead
                        onClick={() => toggleReadStatus(message.id)}
                        hoverLabel="Marquer comme non lu"
                      />
                    ) : (
                      <ButtonEyeNoRead
                        onClick={() => toggleReadStatus(message.id)}
                        hoverLabel="Marquer comme lu"
                      />
                    )}
                    <ButtonDelete
                      onClick={() => handleDeleteMessage(message.id)}
                      hoverLabel="Supprimer"
                    />
                  </div>
                </div>
                <div className="text-xs text-slate-700 dark:text-slate-200">
                  <div className="font-semibold">{message.name}</div>
                  <div className="text-xs text-primary font-bold italic">
                    {message.email}
                  </div>
                  <div className="mt-1">{message.message}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ContactFormDisplay;
